import { pipelineReport } from './pipelineReport';
import { currentJobPipelineReport } from './currentJobPipelineReport';
import { hireReport } from './hireReport';
import { contactsReport } from './contactsReport';
import { jobsReports } from './jobsReports';
import { rejectionsReports } from './rejectionsReports';
import { offerReports } from './offerReports';
import { interviewReports } from './interviewReports';
import { eeoReports } from './eeoReports';
import { flowReports } from './flowReports';
import { performanceReport } from './performanceReport';
import { requisitionsReport } from './requisitionsReports';

const BGV_RISK_LEVEL_FILTER = 'bgv_risk_level';

type ReportConfig = {
  layout: unknown; // used to render in builder engine
  title: string; // used to render in header
  refreshScope: string; // used to fetch last refreshed time
  filters: readonly string[]; // used to render filters to show
};

/**
 * Returns filters to show for a report. Excludes `bgv_risk_level` when feFraudDetection is disabled.
 */
export const getFiltersToShow = (
  filters: readonly string[],
  feFraudDetection: boolean
): string[] =>
  feFraudDetection
    ? [...filters]
    : filters.filter((f) => f !== BGV_RISK_LEVEL_FILTER);

export const STATIC_REPORT_UI_RENDER_CONFIGS: Record<string, ReportConfig> = {
  live_job_pipeline: {
    layout: currentJobPipelineReport,
    title: 'Current job pipeline',
    refreshScope: 'applications',
    filters: [
      'job_name',
      'job_type',
      'time_in_stage',
      'is_overdue',
      'primary_recruiter',
      'primary_hm',
      'appl_contact_source',
      'appl_credited_to',
      'department',
      'office',
      'application_creator',
      'bgv_risk_level',
    ],
  },
  pipeline: {
    layout: pipelineReport,
    title: 'Pipeline',
    refreshScope: 'applications',
    filters: [
      'job_name',
      'job_type',
      'job_status',
      'primary_recruiter',
      'primary_hm',
      'appl_contact_source',
      'department',
      'appl_location',
      'appl_stage',
      'application_creator',
      'bgv_risk_level',
    ],
  },
  hires: {
    layout: hireReport,
    title: 'Hires',
    refreshScope: 'applications',

    filters: [
      'job_name',
      'job_type',
      'job_status',
      'primary_recruiter',
      'primary_hm',
      'appl_contact_source',
      'department',
      'appl_location',
      'appl_credited_to',
      'hired_by',
    ],
  },
  contacts: {
    layout: contactsReport,
    title: 'Contacts',
    filters: [
      'job_name',
      'contact_source',
      'contact_credited_to',
      'contact_created_by',
      'bgv_risk_level',
    ],
    refreshScope: 'contacts',
  },
  jobs: {
    layout: jobsReports,
    title: 'Jobs',
    refreshScope: 'jobs',
    filters: [
      'job_name',
      'job_type',
      'job_status',
      'primary_recruiter',
      'primary_hm',
      'department',
      'job_location',
      'close_reason',
      'created_by',
    ],
  },
  rejections: {
    layout: rejectionsReports,
    title: 'Rejections',
    refreshScope: 'applications',
    filters: [
      'job_name',
      'job_type',
      'job_status',
      'primary_recruiter',
      'primary_hm',
      'appl_contact_source',
      'department',
      'appl_location',
      'appl_credited_to',
      'rejection_reason',
      'rejection_type',
      'rejected_stage',
      'rejected_by',
    ],
  },
  offers: {
    layout: offerReports,
    title: 'Offers',
    refreshScope: 'applications',

    filters: [
      'job_name',
      'job_type',
      'job_status',
      'primary_recruiter',
      'primary_hm',
      'department',
      'appl_contact_source',
      'offer_by',
    ],
  },
  interviews: {
    layout: interviewReports,
    title: 'Interviews',
    refreshScope: 'interviews',
    filters: [
      'job_name',
      'job_type',
      'job_status',
      'primary_recruiter',
      'primary_hm',
      'appl_contact_source',
      'department',
      'appl_location',
      'interview_type',
      'duration_bucket',
      'scheduled_by',
    ],
  },
  eeo: {
    layout: eeoReports,
    title: 'EEO',
    refreshScope: 'applications',

    filters: [
      'job_name',
      'job_type',
      'job_status',
      'primary_recruiter',
      'primary_hm',
      'department',
      'appl_contact_source',
      'appl_location',
      'gender',
      'race',
      'veteran_status',
      'disability_status',
      'users',
    ],
  },
  'flow-health': {
    layout: flowReports,
    refreshScope: 'applications',
    title: 'Flow Health',
    filters: ['job_name', 'recruiter_id', 'flow_id'],
  },
  'recruiter-performance': {
    layout: performanceReport,
    title: 'Recruiter performance',
    refreshScope: 'applications',
    filters: [
      'users',
      'user_role',
      'job_name',
      'job_type',
      'job_status',
      'primary_recruiter',
      'primary_hm',
      'appl_contact_source',
      'department',
      'appl_location',
    ],
  },
  requisitions: {
    layout: requisitionsReport,
    title: 'Requisitions',
    refreshScope: 'applications',
    filters: [
      'requisition_type',
      'requisition_status',
      'recruiter',
      'hiring_manager',
      'department',
      'office',
      'work_type',
      'associated_job_name',
      'associated_job_status',
      'requester',
      'users',
    ],
  },
};
